import {
    api,
    apiErrorMessage,
    browserQuery,
    formatMoney,
    formatTime,
    getTimezone,
    oneSpec,
    useFreshQuery,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    PageHeader,
    SearchableSelect,
    Skeleton,
    type SelectOption,
} from '@bemor/web-kit/ui';
import { useSearchParams } from 'react-router-dom';
// The kit's theme maps --chart-1..4 onto themselves (theme-commerce.css), so they compute empty in the apps;
// the fallbacks are the design tokens behind them (black, signal, gray-400, gray-200).
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { Merchant, MetricPoint, Metrics } from '@/api/types';

/* BM-122 (decision 3, on Nexroll's profit-report chart shell): the tenant's or one merchant's metrics over the last
   hour, day or week by minute or hour: six line charts over the minute buckets and the NMI HTTP log, the decline mix,
   and the window's totals as tiles. Refetched every minute. */

const PATH = '/staff/reports/metrics';
const WINDOWS: SelectOption[] = [
    { value: '1h', label: 'Last hour' },
    { value: '24h', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
];
const STEPS: SelectOption[] = [
    { value: 'minute', label: 'By minute' },
    { value: 'hour', label: 'By hour' },
];

function metricsQuery(params: URLSearchParams) {
    const window = params.get('window') ?? '24h';
    return {
        window,
        step: params.get('step') ?? (window === '1h' ? 'minute' : 'hour'),
        merchant_id: params.get('merchant_id') ?? '',
    };
}

/** The reads of `/reports/metrics`, shared with the route's prefetch. */
export const metricsReads = (url: URL) => ({
    metrics: oneSpec(PATH, metricsQuery(url.searchParams)),
});

const loadMerchants = async (query: string): Promise<SelectOption[]> =>
    (
        await api().get<Merchant[]>('/staff/merchants', {
            params: { q: query, per_page: 20 },
        })
    ).map((m) => ({
        value: m.id,
        label: m.display_name ?? m.legal_name ?? m.id,
    }));

const bps = (v: number | null | undefined) =>
    v == null ? '—' : `${(v / 100).toFixed(1)}%`;
const ms = (v: number | null | undefined) => (v == null ? '—' : `${v} ms`);

function Tile({
    label,
    value,
    testId,
    loading,
}: {
    label: string;
    value: string;
    testId: string;
    loading: boolean;
}) {
    return (
        <Card className="gap-0 py-3" data-testid={testId}>
            <CardContent className="px-4 py-0">
                <span className="text-fg-2 text-xs font-medium">{label}</span>
                <p className="text-fg-1 mt-1.5 text-lg leading-tight font-bold tabular-nums">
                    {loading ? <Skeleton className="h-[1em] w-16" /> : value}
                </p>
            </CardContent>
        </Card>
    );
}

type Series = {
    key: keyof MetricPoint;
    name: string;
    stroke: string;
    format?: (v: number) => string;
}[];

function Chart({
    title,
    points,
    series,
    step,
}: {
    title: string;
    points: readonly MetricPoint[];
    series: Series;
    step: string;
}) {
    const zone = getTimezone();
    const data = points.map((p) => ({ ...p, label: formatTime(p.at, zone) }));
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{title}</CardTitle>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                        data={data}
                        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--chart-grid)"
                        />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: 'var(--fg-2)' }}
                            minTickGap={step === 'minute' ? 40 : 20}
                        />
                        <YAxis
                            tick={{ fontSize: 11, fill: 'var(--fg-2)' }}
                            tickFormatter={series[0].format}
                            width={56}
                        />
                        <Tooltip
                            formatter={(value, name) => {
                                const s = series.find((x) => x.name === name);
                                return [
                                    s?.format
                                        ? s.format(Number(value))
                                        : String(value),
                                    String(name),
                                ];
                            }}
                        />
                        {series.map((s) => (
                            <Line
                                isAnimationActive={false}
                                key={s.key}
                                type="monotone"
                                dataKey={s.key}
                                name={s.name}
                                stroke={s.stroke}
                                strokeWidth={2}
                                dot={false}
                                connectNulls
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}

export function MetricsPage() {
    const [params, setParams] = useSearchParams();
    const q = metricsQuery(params);
    const set = (patch: Record<string, string | null>) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value);
            else next.delete(key);
        }
        setParams(next, { replace: true });
    };
    // a live page: re-read every minute (the buckets are minutes)
    const query = useFreshQuery<Metrics>({
        ...browserQuery<Metrics>(oneSpec(PATH, q)),
        refetchInterval: 60_000,
    });
    const m = query.data?.data;
    const t = m?.totals;
    const points = m?.series ?? [];
    const rate = (v: number) => `${(v / 100).toFixed(0)}%`;
    const dollars = (v: number) => formatMoney(v, 'USD');
    return (
        <div className="flex flex-col gap-4" data-testid="metrics-page">
            <PageHeader
                title="Metrics"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            The buckets are kept 35 days, so a week is the
                            longest window. Times are yours; the figures the
                            tenant&apos;s.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <div
                className="grid grid-cols-1 gap-3 md:grid-cols-3"
                data-testid="metrics-filters"
            >
                <SearchableSelect
                    label="Window"
                    options={WINDOWS}
                    value={q.window}
                    onChange={(v) =>
                        set({ window: v, step: v === '7d' ? 'hour' : null })
                    }
                    testId="metrics-window"
                />
                <SearchableSelect
                    label="Step"
                    options={
                        q.window === '7d'
                            ? STEPS.filter((s) => s.value === 'hour')
                            : STEPS
                    }
                    value={q.step}
                    onChange={(v) => set({ step: v })}
                    testId="metrics-step"
                />
                <SearchableSelect
                    label="Merchant"
                    loadOptions={loadMerchants}
                    value={q.merchant_id || null}
                    onChange={(v) => set({ merchant_id: v })}
                    clearable
                    placeholder="The tenant"
                    testId="metrics-merchant"
                />
            </div>
            {query.error ? (
                <p role="alert" className="text-danger-fg text-sm">
                    {apiErrorMessage(query.error)}
                </p>
            ) : null}
            <div
                className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7"
                data-testid="metrics-totals"
            >
                <Tile
                    label="Attempts"
                    value={String(t?.attempts ?? 0)}
                    testId="metric-attempts"
                    loading={query.isPending}
                />
                <Tile
                    label="Approval rate"
                    value={bps(t?.approval_rate_bps)}
                    testId="metric-approval"
                    loading={query.isPending}
                />
                <Tile
                    label="Gross"
                    value={formatMoney(t?.gross_cents ?? 0, 'USD')}
                    testId="metric-gross"
                    loading={query.isPending}
                />
                <Tile
                    label="Conversion"
                    value={bps(t?.conversion_bps)}
                    testId="metric-conversion"
                    loading={query.isPending}
                />
                <Tile
                    label="NMI p50"
                    value={ms(t?.nmi_p50_ms)}
                    testId="metric-p50"
                    loading={query.isPending}
                />
                <Tile
                    label="NMI p95"
                    value={ms(t?.nmi_p95_ms)}
                    testId="metric-p95"
                    loading={query.isPending}
                />
                <Tile
                    label="NMI errors"
                    value={bps(t?.nmi_error_rate_bps)}
                    testId="metric-errors"
                    loading={query.isPending}
                />
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Chart
                    title="Attempts and approved"
                    points={points}
                    step={q.step}
                    series={[
                        {
                            key: 'attempts',
                            name: 'Attempts',
                            stroke: 'var(--chart-1, var(--fg-1))',
                        },
                        {
                            key: 'approved',
                            name: 'Approved',
                            stroke: 'var(--success-solid)',
                        },
                    ]}
                />
                <Chart
                    title="Approval rate"
                    points={points}
                    step={q.step}
                    series={[
                        {
                            key: 'approval_rate_bps',
                            name: 'Approval rate',
                            stroke: 'var(--chart-2, var(--signal-500))',
                            format: rate,
                        },
                    ]}
                />
                <Chart
                    title="Gross"
                    points={points}
                    step={q.step}
                    series={[
                        {
                            key: 'gross_cents',
                            name: 'Gross',
                            stroke: 'var(--chart-3, var(--gray-400))',
                            format: dollars,
                        },
                    ]}
                />
                <Chart
                    title="Conversion"
                    points={points}
                    step={q.step}
                    series={[
                        {
                            key: 'conversion_bps',
                            name: 'Conversion',
                            stroke: 'var(--chart-4, var(--gray-200))',
                            format: rate,
                        },
                    ]}
                />
                <Chart
                    title="NMI latency"
                    points={points}
                    step={q.step}
                    series={[
                        {
                            key: 'nmi_p50_ms',
                            name: 'p50',
                            stroke: 'var(--chart-1, var(--fg-1))',
                            format: (v) => `${v} ms`,
                        },
                        {
                            key: 'nmi_p95_ms',
                            name: 'p95',
                            stroke: 'var(--warning-solid)',
                            format: (v) => `${v} ms`,
                        },
                    ]}
                />
                <Chart
                    title="NMI calls and errors"
                    points={points}
                    step={q.step}
                    series={[
                        {
                            key: 'nmi_calls',
                            name: 'Calls',
                            stroke: 'var(--chart-1, var(--fg-1))',
                        },
                        {
                            key: 'nmi_errors',
                            name: 'Errors',
                            stroke: 'var(--danger-solid)',
                        },
                    ]}
                />
            </div>
            <Card data-testid="decline-mix">
                <CardHeader>
                    <CardTitle className="text-base">Decline mix</CardTitle>
                </CardHeader>
                <CardContent>
                    {m && m.decline_mix.length === 0 ? (
                        <p className="text-fg-2 py-4 text-center text-sm">
                            No decline in the window.
                        </p>
                    ) : (
                        <ResponsiveContainer
                            width="100%"
                            height={Math.max(
                                160,
                                (m?.decline_mix.length ?? 0) * 32 + 40,
                            )}
                        >
                            <BarChart
                                data={[...(m?.decline_mix ?? [])]}
                                layout="vertical"
                                margin={{
                                    top: 0,
                                    right: 20,
                                    left: 0,
                                    bottom: 0,
                                }}
                            >
                                <XAxis
                                    type="number"
                                    tick={{
                                        fontSize: 11,
                                        fill: 'var(--fg-2)',
                                    }}
                                    allowDecimals={false}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="category"
                                    width={160}
                                    tick={{ fontSize: 11 }}
                                />
                                <Tooltip />
                                <Bar
                                    isAnimationActive={false}
                                    dataKey="count"
                                    name="Declines"
                                    fill="var(--danger-solid)"
                                    radius={[0, 4, 4, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
